import { motion, useReducedMotion } from "framer-motion";

/**
 * A quiet typographic band presenting the word "welcome" across several
 * writing systems — a nod to the many languages MELT serves.
 * Each word carries a lang attribute for correct pronunciation by screen readers,
 * and the group is labelled so the meaning is clear.
 */
const WORDS = [
  { text: "Welcome", lang: "en", label: "Welcome (English)", font: '"Noto Serif", serif' },
  { text: "مرحباً", lang: "ar", label: "Marhaban (Arabic)", font: '"Noto Naskh Arabic", serif' },
  { text: "स्वागत है", lang: "hi", label: "Swagat hai (Hindi, Devanagari)", font: '"Noto Sans Devanagari", sans-serif' },
  { text: "欢迎", lang: "zh", label: "Huānyíng (Chinese)", font: '"Noto Serif SC", serif' },
  { text: "வரவேற்பு", lang: "ta", label: "Varaverppu (Tamil)", font: '"Noto Sans Tamil", sans-serif' },
  { text: "ಸ್ವಾಗತ", lang: "kn", label: "Swagata (Kannada)", font: '"Noto Sans Kannada", sans-serif' },
  { text: "స్వాగతం", lang: "te", label: "Swagatam (Telugu)", font: '"Noto Sans Telugu", sans-serif' },
  { text: "خوش آمدید", lang: "fa", label: "Khosh amadid (Persian)", font: '"Noto Naskh Arabic", serif' },
  { text: "Добро пожаловать", lang: "ru", label: "Dobro pozhalovat (Russian, Cyrillic)", font: '"Noto Serif", serif' },
  { text: "환영합니다", lang: "ko", label: "Hwan-yeong-hamnida (Korean)", font: '"Noto Sans KR", sans-serif' },
];

export const MultilingualWelcome = ({ tone = "light" }) => {
  const reduce = useReducedMotion();
  const color = tone === "light" ? "rgba(250,248,245,0.92)" : "#2E2A5B";
  const divider = tone === "light" ? "rgba(224,164,88,0.55)" : "rgba(15,124,123,0.4)";

  return (
    <ul
      className="flex flex-wrap items-center justify-center gap-x-6 gap-y-4 md:gap-x-10"
      aria-label="The word welcome shown in several writing systems"
      data-testid="multilingual-welcome-band"
    >
      {WORDS.map((w, i) => (
        <motion.li
          key={w.lang}
          className="flex items-center gap-6 md:gap-10"
          initial={reduce ? false : { opacity: 0, y: 10 }}
          animate={reduce ? {} : { opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.15 + i * 0.12 }}
        >
          <span
            lang={w.lang}
            aria-label={w.label}
            className="text-lg md:text-2xl whitespace-nowrap"
            style={{ fontFamily: w.font, color }}
          >
            {w.text}
          </span>
          {i < WORDS.length - 1 && (
            <span aria-hidden="true" className="hidden md:inline-block h-4 w-px" style={{ backgroundColor: divider }} />
          )}
        </motion.li>
      ))}
    </ul>
  );
};

export default MultilingualWelcome;
